import { useMemo, useRef } from 'react';

import {
  useGetIntakeChaseQuery,
  useStartIntakeChaseMutation,
  useUpdateIntakeChaseStatusMutation,
} from '@/services/api/intakeChaseService';
import type {
  IntakeChase,
  IntakeChaseMessage,
} from '@/services/types/intake-chase-types';
import {
  IntakeChaseMessageStatus,
  IntakeChaseStatus,
} from '@/services/types/intake-chase-types';

/** Status values where the chase is still in-flight and worth polling. */
const POLLABLE_STATUSES = new Set<string>([
  IntakeChaseStatus.ACTIVE,
  IntakeChaseStatus.PAUSED,
  IntakeChaseStatus.NEEDS_ATTENTION,
]);

export interface ChaseComputed {
  currentDay: number;
  currentChase: number;
  totalMessagesSent: number;
  totalMessages: number;
  failedMessageCount: number;
  failureReason: string | null;
  progressPercent: number;
  nextMessage: IntakeChaseMessage | null;
  canStart: boolean;
  canPause: boolean;
  canResume: boolean;
  canStop: boolean;
}

/**
 * Pure computation of derived chase state.
 * Shared between the `useAutoChase` hook and table-cell components
 * that receive a chase object directly (without the hook).
 */
export function computeChaseState(chase: IntakeChase | null | undefined): ChaseComputed {
  if (!chase) {
    return {
      currentDay: 0,
      currentChase: 0,
      totalMessagesSent: 0,
      totalMessages: 0,
      failedMessageCount: 0,
      failureReason: null,
      progressPercent: 0,
      nextMessage: null,
      canStart: true,
      canPause: false,
      canResume: false,
      canStop: false,
    };
  }

  const messages = chase.messages || [];
  const hasMessages = messages.length > 0;

  // Use full messages array if available, otherwise fall back to summary fields
  const totalMessages = hasMessages ? messages.length : (chase.message_count ?? 0);
  const totalMessagesSent = hasMessages
    ? messages.filter((m) => m.status === IntakeChaseMessageStatus.SENT).length
    : (chase.sent_count ?? 0);
  const failedMessages = hasMessages
    ? messages.filter((m) => m.status === IntakeChaseMessageStatus.FAILED)
    : [];
  const failedMessageCount = hasMessages
    ? failedMessages.length
    : (chase.failed_count ?? 0);
  const failureReason = hasMessages
    ? (failedMessages.find((m) => m.failure_reason)?.failure_reason ?? null)
    : (chase.latest_failure_reason ?? null);
  const progressPercent =
    totalMessages > 0 ? Math.round((totalMessagesSent / totalMessages) * 100) : 0;

  let currentDay: number;
  let currentChase: number;
  let nextMessage: IntakeChaseMessage | null;

  if (hasMessages) {
    // Compute from full messages array
    const sentMessages = messages.filter(
      (m) => m.status === IntakeChaseMessageStatus.SENT,
    );
    const lastSent = sentMessages.length > 0
      ? sentMessages[sentMessages.length - 1]
      : null;

    nextMessage = messages.find(
      (m) =>
        m.status === IntakeChaseMessageStatus.PENDING ||
        m.status === IntakeChaseMessageStatus.SENDING,
    ) ?? null;

    currentDay = nextMessage?.day_number ?? lastSent?.day_number ?? 0;
    currentChase = nextMessage?.chase_number ?? lastSent?.chase_number ?? 0;
  } else {
    // Use summary fields from list serializer
    currentDay = chase.current_day ?? 0;
    currentChase = chase.current_chase ?? 0;
    nextMessage = null;
  }

  const { status } = chase;
  const isActive = status === IntakeChaseStatus.ACTIVE;
  const isPaused = status === IntakeChaseStatus.PAUSED;
  const isNeedsAttention = status === IntakeChaseStatus.NEEDS_ATTENTION;
  const isCompleted = status === IntakeChaseStatus.COMPLETED;
  const isStopped = status === IntakeChaseStatus.STOPPED;

  return {
    currentDay,
    currentChase,
    totalMessagesSent,
    totalMessages,
    failedMessageCount,
    failureReason,
    progressPercent,
    nextMessage,
    canStart: !chase || isCompleted || isStopped,
    canPause: isActive,
    canResume: isPaused || isNeedsAttention,
    canStop: isActive || isPaused || isNeedsAttention,
  };
}

export function useAutoChase(intakeId: string) {
  // Track the last-known chase status so we can stop polling for terminal states.
  // Using a ref avoids a circular dependency (query result → pollingInterval → query).
  const lastStatusRef = useRef<string | undefined>(undefined);

  const shouldPoll =
    !!intakeId &&
    (!lastStatusRef.current || POLLABLE_STATUSES.has(lastStatusRef.current));

  const {
    data: chase,
    isLoading,
  } = useGetIntakeChaseQuery(intakeId, {
    skip: !intakeId,
    pollingInterval: shouldPoll ? 30_000 : 0,
  });

  // Keep the ref in sync with the latest data.
  if (chase?.status !== undefined) {
    lastStatusRef.current = chase.status;
  }
  const [startChase, startResult] = useStartIntakeChaseMutation();
  const [updateStatus, updateResult] = useUpdateIntakeChaseStatusMutation();

  const computed = useMemo(() => computeChaseState(chase), [chase]);

  return {
    chase,
    isLoading,
    startChase,
    updateStatus,
    isStarting: startResult.isLoading,
    isUpdating: updateResult.isLoading,
    ...computed,
  };
}
